/**
 * Candidate Kanban card — stage-aware hierarchy matching the InVirtus pipeline UX.
 *
 * Structure: selection gutter → avatar → name (+ cheating/expiry tags) / email →
 * Match/Fit/source badges → stage primary CTA (via CandidateActions) + more menu.
 * Card actions are icon-only with hover tooltips — a column is too narrow for
 * labels like "Send Interview Invitation". No card-level tooltips.
 */

import type { ReactNode } from 'react';
import { ClockAlert, FileText } from 'lucide-react';
import { Tag } from '@/components/ui/atoms/Tag';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/atoms/Typography';
import { Card } from '@/components/ui/atoms/Card';
import { Avatar } from '@/components/ui/atoms/Avatar';
import { SuspiciousActivityBadge } from '@/components/ui/SuspiciousActivityBadge';
import { kanbanSelectionGutterClass } from '@/components/ui/organisms/Kanban';
import { CandidateActions } from '../CandidateActions';
import { CandidateEmailInline } from '../CandidateEmailInline';
import type { Candidate, JobOffer } from '@/models/job';
import { isAssessedStageSemantic } from '@/utils/pipeline-stages';
import type { ExamConfig } from '@/types/job.types';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/date';
import { cn } from '@/utils/cn';
import {
  formatCvMatchScore,
  formatPercentScore,
  isInterviewExpired,
} from '../candidates-table/candidatesTable.utils';
import { sourceLabelKey } from '../candidates-table/candidatesTable.columns';
import { getMatchScoreIntent, getScoreIntent } from '@/utils/tag.utils';

export interface CandidateKanbanCardContentProps {
  candidate: Candidate;
  job: JobOffer;
  examConfig: ExamConfig;
  fitScore?: number | null;
  hasCheatingFlag?: boolean;
  requiresReview?: boolean;
  onStatusUpdate?: () => void;
  onCandidateClick?: (candidate: Candidate) => void;
  onDeleteCandidate?: (candidateId: string, candidateName: string) => void;
  onExtendInterview?: (
    interviewId: string,
    candidateName: string,
    currentExpiration: string | null
  ) => void;
  presentation?: 'board' | 'overlay';
  selectionControl?: ReactNode | null;
}

export function CandidateKanbanCardContent({
  candidate,
  job,
  examConfig,
  fitScore = null,
  hasCheatingFlag = false,
  requiresReview,
  onStatusUpdate,
  onCandidateClick,
  onDeleteCandidate,
  onExtendInterview,
  presentation = 'board',
  selectionControl = null,
}: CandidateKanbanCardContentProps) {
  const { t } = useTranslation(['job', 'candidates']);
  const isOverlay = presentation === 'overlay';
  const isInterviewing = candidate.pipelineStageSemantic === 'interviewing';
  const showFitScore =
    fitScore != null &&
    (isAssessedStageSemantic(candidate.pipelineStageSemantic) ||
      Boolean(candidate.hasAssessmentAvailable));
  const expiresAt = candidate.interviewExpiresAt || null;
  const expired = isInterviewing && isInterviewExpired(expiresAt);
  const sourceLabel = t(sourceLabelKey(candidate.source || 'cv_upload'));

  const openDetails = () => onCandidateClick?.(candidate);

  return (
    <Card
      variant="custom"
      padding="none"
      rounded="lg"
      data-kanban-card=""
      className="!m-0 w-full border border-dark-invirtus-soft/80 bg-white shadow-sm"
      interactive={!isOverlay}
      onClick={isOverlay ? undefined : openDetails}
    >
      <div
        className={
          selectionControl
            ? 'grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-2.5'
            : 'flex flex-col gap-2.5'
        }
      >
        {selectionControl ? (
          <div
            className={`${kanbanSelectionGutterClass} row-start-1 self-start pt-1.5`}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {selectionControl}
          </div>
        ) : null}

        <div
          className={cn(
            'flex min-w-0 flex-col gap-2.5',
            selectionControl && 'col-start-2 row-start-1'
          )}
        >
          {/* Identity row: avatar + name */}
          <div className="flex min-w-0 items-start gap-2.5">
            <Avatar
              initials={candidate.talentName || '?'}
              size="sm"
              variant="neutral"
              alt={candidate.talentName}
              className="mt-0.5 shrink-0 max-md:hidden"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <Typography
                  variant="body-sm"
                  weight="semibold"
                  color="primary"
                  className="min-w-0 truncate leading-5"
                >
                  {candidate.talentName || 'Unknown'}
                </Typography>
                {hasCheatingFlag ? (
                  <SuspiciousActivityBadge size="sm" />
                ) : null}
                {expired && expiresAt ? (
                  <Tooltip
                    content={`${t('candidates:link_expired_on')} · ${formatDate(expiresAt)}`}
                    variant="bottom"
                    triggerClassName="shrink-0"
                  >
                    <Tag
                      intent="error"
                      variant="subtle"
                      size="sm"
                      icon={<ClockAlert />}
                      role="img"
                      aria-label={t('candidates:link_expired_on')}
                      className="w-5 !gap-0 !px-0"
                    >
                      {null}
                    </Tag>
                  </Tooltip>
                ) : null}
              </div>
              <CandidateEmailInline
                jobId={job.id}
                candidateId={candidate.id}
                hasValidEmail={Boolean(candidate.hasValidEmail)}
                email={candidate.talentEmail}
                stopPropagation
                showNativeTitle={false}
                className="min-w-0"
              />
            </div>
          </div>

          {/* Metrics: Match prominent, Fit secondary, source as document badge */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Tag
              intent={
                candidate.score != null
                  ? getMatchScoreIntent(Math.round(candidate.score / 100))
                  : 'secondary'
              }
              variant="subtle"
              size="sm"
            >
              {candidate.score != null
                ? `${formatCvMatchScore(candidate.score)} ${t('table.col_match')}`
                : `— ${t('table.col_match')}`}
            </Tag>
            {showFitScore ? (
              <Tag
                intent={getScoreIntent(fitScore)}
                variant="subtle"
                size="sm"
              >
                {`${formatPercentScore(fitScore)} ${t('table.col_fit')}`}
              </Tag>
            ) : null}
            <Tag
              intent="secondary"
              variant="subtle"
              size="sm"
              icon={<FileText className="h-3 w-3" aria-hidden />}
              className="max-w-[9rem] truncate"
            >
              {sourceLabel}
            </Tag>
          </div>

          {/* Stage actions — isolated from card click/drag */}
          <div
            className={cn(
              'border-t border-dark-invirtus-soft/60 mt-0.5 pt-2.5',
              isOverlay && 'pointer-events-none'
            )}
            onClick={
              isOverlay ? undefined : (event) => event.stopPropagation()
            }
            onPointerDown={
              isOverlay ? undefined : (event) => event.stopPropagation()
            }
          >
            <CandidateActions
              candidate={candidate}
              job={job}
              examConfig={examConfig}
              requiresReview={requiresReview}
              onStatusUpdate={onStatusUpdate}
              onDeleteCandidate={onDeleteCandidate}
              onExtendInterview={onExtendInterview}
              onViewDetails={openDetails}
              variant="kanban"
              origin="kanban"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
